// src/components/Inventory/InventoryCounters.js
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Card from "../ui/Card";

import { useTranslation } from "react-i18next";

import {
  faUsers,
  faDollarSign,
  faBoxOpen,
  faTruckFast,
  faTrophy,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";

/* ---------- helpers ---------- */
function toDateOnly(d) {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return null;
  x.setHours(0, 0, 0, 0);
  return x;
}

function pickMaterial(listing) {
  return (
    listing?.material ||
    listing?.materialType ||
    listing?.material_name ||
    listing?.material_type ||
    ""
  );
}

function pickDeliveredLbs(listing, request) {
  const toLbs = (n) => Number(n) * 2.2046226218;

  const lbsCandidates = [
    request?.quantity_delivered_lbs,
    request?.delivered_quantity_lbs,
    request?.delivered_lbs,
    request?.qty_delivered_lbs,
    request?.quantity_delivered,
    request?.quantity,
    request?.qty,
    listing?.quantity_lbs,
    listing?.total_qty_lbs,
    listing?.quantity,
    listing?.qty,
  ];

  for (const v of lbsCandidates) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }

  const kgCandidates = [
    request?.delivered_weight_kg,
    request?.weight_kg,
    request?.quantity_delivered_kg,
    listing?.weight_kg,
    listing?.quantity_kg,
    listing?.total_weight_kg,
  ];

  for (const v of kgCandidates) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return toLbs(n);
  }

  return 0;
}

function pickDonorKey(listing, request) {
  return (
    listing?.donor_id ||
    listing?.company_id ||
    listing?.organization_id ||
    listing?.donor_name ||
    listing?.company_name ||
    listing?.company ||
    request?.donor_id ||
    request?.donor_name ||
    ""
  );
}

function pickCostPerLb(listing, request) {
  const candidates = [
    request?.cost_per_lb,
    request?.costPerLb,
    listing?.cost_per_lb,
    listing?.costPerLb,
    listing?.cost,
    request?.cost,
  ];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

/* ---------- component ---------- */
function InventoryCounters({
  listings = [],
  requests = [],
  dateFrom,
  dateTo,
  materials = [],
  darkMode,
  isLoading = false,
  justUpdated = false,
}) {
  const { t, i18n } = useTranslation(); // ✅ ADD

  function money(n) {
    const num = Number(n) || 0;

    const locale = i18n.language || undefined;

    return num.toLocaleString(locale, {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const computed = useMemo(() => {
    function pickDonorLabel(listing, request) {
      return (
        listing?.donor_name ||
        listing?.company_name ||
        listing?.company ||
        request?.donor_name ||
        t("Unknown")
      );
    }

    const listingById = new Map();
    (listings || []).forEach((l) => {
      if (l?.id != null) listingById.set(String(l.id), l);
    });

    const from = dateFrom ? toDateOnly(dateFrom) : null;
    const to = dateTo ? toDateOnly(dateTo) : null;
    const allowMaterials = new Set(materials || []);

    const delivered = (requests || []).filter((r) => {
      if (!r?.date_delivered) return false;

      const deliveredDate = toDateOnly(r.date_delivered);
      if (!deliveredDate) return false;

      if (from && deliveredDate < from) return false;
      if (to && deliveredDate > to) return false;

      if (allowMaterials.size > 0) {
        const listing = listingById.get(String(r.listing_id));
        const mat = pickMaterial(listing);
        if (!allowMaterials.has(mat)) return false;
      }

      return true;
    });

    let totalReceivedLbs = 0;
    let totalPickups = 0;
    let costSpent = 0;

    const donorAgg = new Map();

    for (const r of delivered) {
      const listing = listingById.get(String(r.listing_id));
      if (!listing) continue;

      const lbs = pickDeliveredLbs(listing, r);
      if (lbs <= 0) continue;

      totalPickups += 1;
      totalReceivedLbs += lbs;

      const donorKey = String(pickDonorKey(listing, r) || "unknown");
      const donorLabel = pickDonorLabel(listing, r);

      const prev = donorAgg.get(donorKey);
      if (prev) donorAgg.set(donorKey, { label: prev.label, lbs: prev.lbs + lbs });
      else donorAgg.set(donorKey, { label: donorLabel, lbs });

      const cpl = pickCostPerLb(listing, r);
      if (cpl > 0) costSpent += lbs * cpl;
    }

    let topDonor = null;
    donorAgg.forEach((v) => {
      if (!topDonor || v.lbs > topDonor.lbs) topDonor = v;
    });

    return {
      totalReceivedLbs: Math.round(totalReceivedLbs),
      totalPickups,
      uniqueDonors: donorAgg.size,
      topDonorLabel: topDonor?.label || "—",
      topDonorLbs: topDonor ? Math.round(topDonor.lbs) : 0,
      costSpent,
    };
  }, [listings, requests, dateFrom, dateTo, materials, t]);

  const topDonorLbsStr = computed.topDonorLbs.toLocaleString(i18n.language || undefined);

  const topDonorValue =
    computed.topDonorLabel === "—" ? (
      "—"
    ) : (
      <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        {computed.topDonorLabel}
        <span
          title={`${topDonorLbsStr} lbs`}
          style={{ cursor: "help", opacity: 0.45, fontSize: "0.5em", position: "relative", top: "-0.15em" }}
        >
          <FontAwesomeIcon icon={faCircleInfo} />
        </span>
      </span>
    );

  const counterCards = [
    {
      icon: faUsers,
      title: t("Unique Donors"),
      value: computed.uniqueDonors.toLocaleString(i18n.language || undefined),
      description: t("Companies that delivered"),
      colorClass: "counter-blue",
    },
    {
      icon: faTrophy,
      title: t("Top Donor"),
      value: topDonorValue,
      description: t("Highest volume supplier"),
      colorClass: "counter-amber",
    },
    {
      icon: faBoxOpen,
      title: t("Total Received"),
      value: `${computed.totalReceivedLbs.toLocaleString(
        i18n.language || undefined
      )} lbs`,
      description: t("All confirmed deliveries"),
      colorClass: "counter-green",
    },
    {
      icon: faDollarSign,
      title: t("Cost Spent"),
      value: money(computed.costSpent),
      description: t("Estimated material cost"),
      colorClass: "counter-purple",
    },
    {
      icon: faTruckFast,
      title: t("Pickups"),
      value: computed.totalPickups.toLocaleString(i18n.language || undefined),
      description: t("Individual deliveries"),
      colorClass: "counter-green",
    },
  ];

  return (
    <div className="counter-cards-container inv-counters-narrow">
      {counterCards.map((card, index) => (
        <Card
          key={index}
          icon={card.icon}
          value={card.value}
          title={card.title}
          description={card.description}
          variant="counter"
          colorClass={card.colorClass}
          darkMode={darkMode}
          isLoading={isLoading}
          justUpdated={justUpdated}
        />
      ))}
    </div>
  );
}

InventoryCounters.propTypes = {
  listings: PropTypes.array,
  requests: PropTypes.array,
  dateFrom: PropTypes.string,
  dateTo: PropTypes.string,
  materials: PropTypes.array,
  darkMode: PropTypes.bool,
};

export default InventoryCounters;
